import { FC, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router";
import { Button, FormButton } from "../../styles/button";
import {
  ApiError,
  FormGroup,
  InputContainer,
  InputRow,
} from "../../styles/form";
import { SpaceBetweenButtons } from "../../styles/spaces";
import Page from "../layout/Page";
import { handleLoadWorkflowSuggestions } from "../../utils/suggestions";
import Suggestion, { ISuggestionValue } from "../common/suggestion/Suggestion";
import workflowApi from "../../api/workflow";
import {
  IWorkflowQuestion,
  WorkflowSelectedQuestions,
} from "../../models/workflow";
import { IDemand } from "../../models/demand";
import { errorSet } from "../../utils/error";
import { InputLabel } from "../common/form/InputStyles";
import { TailSpin } from "react-loader-spinner";
import { COLORS } from "../../styles/colors";
import {
  getSelectedAnswers,
  isAllQuestionsSelected,
  renderQuestions,
} from "../demand/DemandQuestions";
import demandApi from "../../api/demand";
import { clientDemandListLoad } from "../../store/clientDemandList";
import CustomModal from "../common/modal/CustomModal";
import { SmallTable } from "../common/grid/table";
import { DATE_TIME_FORMAT } from "../../utils/consts";

interface IProps {
  clientDemandListLoad(reload: boolean): void;
}

const ClientDemandNew: FC<IProps> = ({ clientDemandListLoad }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId: paramClientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<
    ISuggestionValue | undefined
  >();
  const [questions, setQuestions] = useState<IWorkflowQuestion[]>([]);
  const [selectedQuestions, setSelectedQuestions] =
    useState<WorkflowSelectedQuestions>({});
  const [conflictDemand, setConflictDemand] = useState<IDemand | null>(null);
  const [conflictDemandLoading, setConflictDemandLoading] = useState(false);
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);

  const canSave =
    !loading &&
    !saving &&
    !!selectedWorkflow &&
    isAllQuestionsSelected(questions, selectedQuestions, null);

  const handleSelectWorkflow = async (value: ISuggestionValue | undefined) => {
    setLoading(true);
    setError(null);
    setSelectedWorkflow(undefined);
    setQuestions([]);

    if (value) {
      try {
        const res = await workflowApi.getQuestions(
          value.id,
          Number.parseInt(paramClientId!)
        );
        setQuestions(res.data);
        setSelectedWorkflow(value);
      } catch (err) {
        errorSet(setError, err, t);
      }
    }

    setLoading(false);
  };

  const handleCreateDemand = async (forceAdd = false) => {
    setSaving(true);
    setError(null);
    try {
      const answers: number[] = [];
      getSelectedAnswers(answers, questions, selectedQuestions, null);

      await demandApi.createDemand(
        selectedWorkflow!.id,
        Number.parseInt(paramClientId!),
        answers,
        forceAdd
      );

      toast.success(t("success.save"));
      handleCloseConflictModal();
      clientDemandListLoad(true);
      navigate(`/client/${paramClientId}/demand`);
    } catch (err: any) {
      if (!forceAdd && err?.response?.status === 409) {
        const conflictDemandId = Number.parseInt(
          err?.response?.data?.errorMessage ?? "",
          10
        );

        if (!Number.isNaN(conflictDemandId)) {
          setConflictDemand(null);
          setConflictDemandLoading(true);
          setIsConflictModalOpen(true);

          try {
            const conflictDemandResponse = await demandApi.getDemand(
              conflictDemandId
            );
            setConflictDemand(conflictDemandResponse.data);
          } catch (conflictErr: any) {
            setIsConflictModalOpen(false);
            errorSet(setError, conflictErr, t);
          }

          setConflictDemandLoading(false);
          setSaving(false);
          return;
        }
      }

      errorSet(setError, err, t);
    }

    setSaving(false);
  };

  const handleSave = async () => {
    await handleCreateDemand();
  };

  const handleCancel = () => {
    navigate(`/client/${paramClientId}/demand`);
  };

  const handleCloseConflictModal = () => {
    setIsConflictModalOpen(false);
    setConflictDemand(null);
    setConflictDemandLoading(false);
  };

  return (
    <Page>
      <h1>{t("demand.detailTitleNew")}</h1>
      <FormGroup>
        <InputRow>
          <InputLabel htmlFor={"workflowId"}>{t("demand.workflow")}</InputLabel>
          <InputContainer>
            <Suggestion
              id="workflowId"
              selected={selectedWorkflow}
              disabledBySelected={!!selectedWorkflow}
              placeholder={t("demand.workflowPlaceholder")}
              loadSuggestions={handleLoadWorkflowSuggestions}
              selectValue={handleSelectWorkflow}
            />
          </InputContainer>
        </InputRow>
        {renderQuestions(
          questions,
          selectedQuestions,
          setSelectedQuestions,
          null
        )}
        {loading && (
          <TailSpin color={COLORS.loaderColor} width={48} height={48} />
        )}
      </FormGroup>
      {error && <ApiError>{error}</ApiError>}
      <FormButton
        type="button"
        ver="secondary"
        disabled={loading}
        onClick={handleCancel}
      >
        {t("common.back")}
      </FormButton>
      <SpaceBetweenButtons />
      <FormButton type="button" disabled={!canSave} onClick={handleSave}>
        {t("common.save")}
      </FormButton>
      <CustomModal
        isOpen={isConflictModalOpen}
        close={handleCloseConflictModal}
        title={t("demand.duplicateWorkflowConfirm")}
        actions={
          <>
            <Button
              ver="secondary"
              disabled={saving}
              onClick={handleCloseConflictModal}
            >
              {t("common.no")}
            </Button>
            <Button disabled={saving} onClick={() => handleCreateDemand(true)}>
              {t("common.yes")}
            </Button>
          </>
        }
      >
        {conflictDemandLoading && (
          <TailSpin color={COLORS.loaderColor} width={48} height={48} />
        )}
        {!conflictDemandLoading && conflictDemand && (
          <SmallTable>
            <thead>
              <tr>
                <th>ID</th>
                <th>{t("demand.createAt")}</th>
                <th>{t("demand.userCreator")}</th>
                <th>{t("demand.state")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <a
                    href={`/client/${paramClientId}/demand/${conflictDemand.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {conflictDemand.id}
                  </a>
                </td>
                <td>
                  {conflictDemand.createdAt
                    ? format(conflictDemand.createdAt, DATE_TIME_FORMAT)
                    : ""}
                </td>
                <td>{conflictDemand.userCreator}</td>
                <td>{t("demand.states." + conflictDemand.state)}</td>
              </tr>
            </tbody>
          </SmallTable>
        )}
      </CustomModal>
    </Page>
  );
};

const mapDispachToProps = {
  clientDemandListLoad,
};

export default connect(null, mapDispachToProps)(ClientDemandNew);
